import React, { useEffect, useState } from "react";
import { api } from "../api";
import NovelExactPdfReviewPaneV1 from "./NovelExactPdfReviewPaneV1";

// Historical browsing never calls prepare, approve, capture or Story save.
// All file bytes come from the EXISTING owner-only final-PDF routes and are
// verified in the browser before being shown or offered for download.
const ID = /^[a-f0-9]{24}$/i;
const SHA = /^[a-f0-9]{64}$/;
const validVersion = v => v && ID.test(String(v.snapshotId || "")) &&
  ID.test(String(v.interiorArtifactId || "")) &&
  SHA.test(String(v.interiorPdfSha256 || "")) &&
  Number.isInteger(v.physicalPageCount) && v.physicalPageCount >= 24 &&
  v.physicalPageCount % 2 === 0 &&
  typeof v.approvedAt === "string" && Number.isFinite(Date.parse(v.approvedAt)) &&
  typeof v.coverApproved === "boolean" &&
  (!v.coverApproved || (ID.test(String(v.coverArtifactId || "")) &&
    SHA.test(String(v.coverPdfSha256 || ""))));

async function verifyOldPdf(blob, expected, actualHeader, minSize = 100) {
  if (!SHA.test(expected) || actualHeader !== expected ||
      !blob || !/^application\/pdf(?:;|$)/i.test(blob.type || "") ||
      blob.size < minSize || blob.size > 8 * 1024 * 1024) {
    throw new Error("Archived PDF identity is invalid.");
  }
  const subtle = typeof window !== "undefined" ? window.crypto?.subtle : null;
  if (!subtle || typeof subtle.digest !== "function") {
    throw new Error("A secure browser is needed to verify historical PDF bytes.");
  }
  const digest = await subtle.digest("SHA-256", await blob.arrayBuffer());
  const actual = [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, "0")).join("");
  if (actual !== expected) throw new Error("Archived PDF bytes do not match their approval.");
}

export default function NovelApprovedVersionsV1({ storyId = "", isFR = false, revisionEnabled = false, onStartRevision }) {
  const label = (en, fr) => isFR ? fr : en;
  const [versions, setVersions] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [files, setFiles] = useState(null);
  const [shown, setShown] = useState("interior");

  useEffect(() => () => {
    if (files?.interiorUrl) URL.revokeObjectURL(files.interiorUrl);
    if (files?.coverUrl) URL.revokeObjectURL(files.coverUrl);
  }, [files]);

  useEffect(() => {
    let active = true;
    setVersions([]); setPage(0); setHasMore(false);
    setSelected(null); setFiles(null); setError("");
    if (!ID.test(storyId)) return () => { active = false; };
    setBusy("index");
    api.getNovelApprovedVersionsV1(storyId,0).then(result => {
      if (!active) return;
      if (result?.ok !== true || result.readOnly !== true ||
          result.publicationEnabled !== false || result.page !== 0 ||
          !Array.isArray(result.versions) || !result.versions.every(validVersion)) {
        throw new Error("Approved version history metadata failed verification.");
      }
      setVersions(result.versions); setHasMore(result.hasMore === true);
    }).catch(e => {
      if (active) setError(String(e?.message || "Approved version history is unavailable."));
    }).finally(() => { if (active) setBusy(""); });
    return () => { active = false; };
  }, [storyId]);

  async function nextPage() {
    if (busy || !hasMore || !ID.test(storyId)) return;
    setBusy("index"); setError("");
    try {
      const result = await api.getNovelApprovedVersionsV1(storyId,page+1);
      if (result?.ok !== true || result.readOnly !== true ||
          result.publicationEnabled !== false || result.page !== page+1 ||
          !Array.isArray(result.versions) || !result.versions.every(validVersion)) {
        throw new Error("The next history page could not be verified.");
      }
      setVersions(old => [...old, ...result.versions]);
      setPage(page+1); setHasMore(result.hasMore === true);
    } catch (e) { setError(String(e?.message || "Could not load older versions.")); }
    finally { setBusy(""); }
  }

  async function openVersion(v) {
    if (busy || !validVersion(v) || !ID.test(storyId)) return;
    setBusy("files"); setError(""); setFiles(null); setSelected(v); setShown("interior");
    try {
      // Do NOT call current-source status: the author may already be revising.
      if (v.coverApproved) {
        const status = await api.getNovelPaperbackCoverStatus(storyId,v.snapshotId,v.interiorArtifactId);
        if (status?.ok !== true || status.state !== "approved_locked" ||
            status.pairAvailable !== true || status.publicationEnabled !== false ||
            status.coverArtifactId !== v.coverArtifactId ||
            status.coverPdfSha256 !== v.coverPdfSha256 ||
            status.exactInteriorPdfSha256 !== v.interiorPdfSha256 ||
            status.physicalPageCount !== v.physicalPageCount) {
          throw new Error("Historical cover and interior approval identities differ.");
        }
        const [interior, cover] = await Promise.all([
          api.getNovelPaperbackCoverPdf(storyId,v.snapshotId,v.interiorArtifactId,"","final-interior"),
          api.getNovelPaperbackCoverPdf(storyId,v.snapshotId,v.interiorArtifactId,"","final-cover"),
        ]);
        if (interior.matchingSha256 !== v.coverPdfSha256 ||
            cover.matchingSha256 !== v.interiorPdfSha256 ||
            interior.printStatus !== "APPROVED-INTERIOR-NOT-PUBLISHED" ||
            cover.printStatus !== "APPROVED-COVER-NOT-PUBLISHED") {
          throw new Error("Historical approved PDF pair does not match.");
        }
        await Promise.all([
          verifyOldPdf(interior.blob,v.interiorPdfSha256,interior.sha256,500),
          verifyOldPdf(cover.blob,v.coverPdfSha256,cover.sha256,500),
        ]);
        setFiles({interiorUrl:URL.createObjectURL(interior.blob),
          coverUrl:URL.createObjectURL(cover.blob)});
      } else {
        const status = await api.getNovelPaperbackInteriorCurrent(storyId,v.snapshotId);
        if (status?.ok !== true || status.state !== "approved_locked" ||
            status.finalPageCountLocked !== true || status.publicationEnabled !== false ||
            status.artifactId !== v.interiorArtifactId ||
            status.pdfSha256 !== v.interiorPdfSha256 ||
            status.physicalPageCount !== v.physicalPageCount) {
          throw new Error("Historical interior approval could not be confirmed.");
        }
        const interior = await api.getNovelPaperbackInteriorPdf(
          storyId,v.snapshotId,v.interiorArtifactId,"final");
        if (Number(interior.lockedPageCount) !== v.physicalPageCount) {
          throw new Error("Historical approved page count differs.");
        }
        await verifyOldPdf(interior.blob,v.interiorPdfSha256,interior.sha256);
        setFiles({interiorUrl:URL.createObjectURL(interior.blob),coverUrl:null});
      }
    } catch (e) { setError(String(e?.message || "Could not verify the selected approved version.")); }
    finally { setBusy(""); }
  }

  return (
    <section data-novel-approved-version-history="read-only"
      style={{display:"grid",gap:14,padding:"18px 20px",border:"1px solid #d7dee6",
        borderRadius:14,background:"#fff"}}>
      <div>
        <div data-novel-history-step="1" style={{fontSize:12,fontWeight:800,
          letterSpacing:"0.1em",textTransform:"uppercase",color:"#64748b",marginBottom:5}}>
          {label("Step 1 · Archive","Étape 1 · Archives")}
        </div>
        <h4 style={{margin:"0 0 6px",fontSize:19,lineHeight:1.3,color:"#1e293b"}}>{label("Approved File History — Read Only","Historique des fichiers approuvés — lecture seule")}</h4>
        <p className="ns-card-meta" style={{margin:0}}>
          {label("Earlier approved files remain available when the working manuscript changes. No edition number or KDP publication is implied.",
            "Les anciens fichiers approuvés restent disponibles lorsque le manuscrit change. Aucun numéro d'édition ni publication KDP n'est implicite.")}
        </p>
      </div>
      {busy === "index" ? <p role="status">{label("Loading approved versions…","Chargement des versions approuvées…")}</p> : null}
      {error ? <p role="alert" style={{color:"#9a3412",overflowWrap:"anywhere"}}>{error}</p> : null}
      {!busy && !error && versions.length === 0
        ? <p className="ns-card-meta">{label("No approved files recorded yet.","Aucun fichier approuvé enregistré pour le moment.")}</p> : null}
      {versions.map(v => (
        <div key={v.interiorArtifactId} data-approved-version-id={v.interiorArtifactId}
          style={{display:"flex",flexWrap:"wrap",gap:8,alignItems:"center",
            justifyContent:"space-between",borderTop:"1px solid #dde8e0",paddingTop:10}}>
          <div style={{minWidth:0}}>
            <strong style={{fontSize:15,color:"#1e293b"}}>{label("Approved on","Approuvé le")} {new Date(v.approvedAt).toLocaleDateString(isFR?"fr-FR":"en-GB")}</strong>
            <div className="ns-card-meta" style={{fontSize:13,marginTop:4}}>{v.physicalPageCount} {label("physical pages","pages physiques")}
              {" · "}{v.coverApproved ? label("Matching cover approved","Couverture correspondante approuvée") :
                label("Interior only","Intérieur seul")}</div>
            <div style={{fontSize:12,color:"#64748b",marginTop:3}}>
              {v.coverApproved ? label("Approved pair · revision-eligible when enabled",
                  "Paire approuvée · révision possible après activation") :
                label("Archived interior · view only","Intérieur archivé · consultation seule")}
            </div>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
            <button type="button" className="ns-button ns-button-outline"
              data-open-approved-version={v.interiorArtifactId}
              disabled={Boolean(busy)} onClick={() => openVersion(v)}>
              {label("Verify and View","Vérifier et voir")}
            </button>
            {revisionEnabled && v.coverApproved && typeof onStartRevision==="function" ? (
              <button type="button" className="ns-button ns-button-primary"
                data-start-private-revision={v.interiorArtifactId}
                disabled={Boolean(busy)} onClick={()=>onStartRevision(v)}>
                {label("Start / Continue Revision","Commencer / Reprendre la révision")}
              </button>
            ) : null}
          </div>
        </div>
      ))}
      {hasMore ? <button type="button" className="ns-button ns-button-outline"
        disabled={Boolean(busy)} onClick={nextPage} data-novel-history-more="true">
        {label("Show Older Versions","Afficher les versions précédentes")}</button> : null}
      {files && selected ? <section data-novel-historical-pdf-verified="true"
        style={{display:"grid",gap:12,borderTop:"2px solid #d7dee6",
          paddingTop:16,marginTop:4}}>
        <h5 style={{fontSize:16,color:"#334155",margin:0}}>
          {label("Verified historical approved file","Fichier ancien approuvé et vérifié")}
        </h5>
        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          <button type="button" className={shown==="interior"?"ns-button ns-button-primary":"ns-button ns-button-outline"}
            onClick={()=>setShown("interior")} aria-pressed={shown==="interior"}>
            {label("Interior","Intérieur")}</button>
          {files.coverUrl ? <button type="button"
            className={shown==="cover"?"ns-button ns-button-primary":"ns-button ns-button-outline"}
            onClick={()=>setShown("cover")} aria-pressed={shown==="cover"}>
            {label("Cover","Couverture")}</button> : null}
          <a className="ns-button ns-button-outline" href={files.interiorUrl}
            download="NextScenes_Historical_Approved_Interior.pdf">
            {label("Download Interior","Télécharger l'intérieur")}</a>
          {files.coverUrl ? <a className="ns-button ns-button-outline" href={files.coverUrl}
            download="NextScenes_Historical_Approved_Cover.pdf">
            {label("Download Cover","Télécharger la couverture")}</a> : null}
        </div>
        <NovelExactPdfReviewPaneV1 key={selected.interiorArtifactId + shown}
          pdfUrl={shown==="cover"?files.coverUrl:files.interiorUrl}
          kind={shown} isFR={isFR} approved />
      </section> : null}
    </section>
  );
}
